/**
 * WebSocket Service
 * Manages bidirectional communication with the server for LLM function calls
 */

export type MessageType = 
  | 'function_call_request'
  | 'function_call_response'
  | 'chat_message'
  | 'connection_established'
  | 'error'
  | 'ping'
  | 'pong';

export interface WebSocketMessage {
  type: MessageType;
  payload: any;
  requestId?: string;
  timestamp?: number;
}

export interface FunctionCallRequest {
  functionName: string;
  parameters: Record<string, any>;
  requestId: string;
}

export interface FunctionCallResponse {
  requestId: string;
  result: any;
  error?: string;
}

type MessageHandler = (message: WebSocketMessage) => void;
type ConnectionHandler = () => void;
type ErrorHandler = (error: Event) => void;
type DebugHandler = (direction: 'sent' | 'received', message: WebSocketMessage) => void;

class WebSocketService {
  private ws: WebSocket | null = null;
  private messageHandlers: Map<MessageType, Set<MessageHandler>> = new Map();
  private onConnectHandlers: Set<ConnectionHandler> = new Set();
  private onDisconnectHandlers: Set<ConnectionHandler> = new Set();
  private onErrorHandlers: Set<ErrorHandler> = new Set();
  private debugHandlers: Set<DebugHandler> = new Set();
  private reconnectTimer: NodeJS.Timeout | null = null;
  private reconnectAttempts = 0;
  // Persistent channel: never give up permanently. Backoff grows
  // exponentially but is capped, with jitter added to avoid thundering
  // herd reconnects; retry count resets on every successful open and on
  // every fresh (non-retry) connect() call triggered by a meaningful
  // token/apiHost change.
  private baseReconnectDelay = 2000;
  private maxReconnectDelay = 30000;
  private isIntentionalClose = false;
  private token: string | undefined;
  private apiHost: string;
  private keepaliveTimer: NodeJS.Timeout | null = null;
  private keepaliveInterval = 30000; // 30 seconds
  private debugMode = false;
  private pendingRequests: Map<string, {
    resolve: (value: any) => void;
    reject: (error: any) => void;
    timeout: NodeJS.Timeout;
  }> = new Map();

  constructor() {
    this.apiHost = '';
  }

  /**
   * Connect to the WebSocket server.
   *
   * `isReconnectAttempt` is set internally by scheduleReconnect() only.
   * Any externally-triggered call (initial mount, or a meaningful
   * apiHost/token change from the host hook) is treated as a fresh
   * connect: it resets the retry/backoff state and tears down any
   * stale socket or pending reconnect timer from a previous target
   * before opening a new one.
   */
  public connect(
    apiHost: string,
    token?: string,
    debugMode: boolean = false,
    isReconnectAttempt: boolean = false
  ): void {
    const targetChanged = apiHost !== this.apiHost || token !== this.token;

    if (!isReconnectAttempt) {
      // Fresh connect request: cancel any in-flight retry loop that was
      // targeting a previous (now stale) host/token and reset backoff.
      this.clearReconnectTimer();
      this.reconnectAttempts = 0;
    }

    if (
      this.ws &&
      !targetChanged &&
      (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)
    ) {
      // Already connected/connecting to the same target - avoid opening
      // a duplicate socket.
      console.log('WebSocket already connected or connecting');
      return;
    }

    if (this.ws && targetChanged) {
      // Target changed (e.g. token refreshed, address/apiHost switched):
      // close the stale socket intentionally so its onclose handler does
      // not itself schedule a competing reconnect.
      this.closeCurrentSocket();
    }

    this.apiHost = apiHost;
    this.token = token;
    this.debugMode = debugMode;
    this.isIntentionalClose = false;

    try {
      // Convert HTTP(S) URL to WS(S) URL
      const wsUrl = this.apiHost.replace(/^http/, 'ws') + '/api/v1/ws';
      const urlWithToken = token ? `${wsUrl}?token=${encodeURIComponent(token)}` : wsUrl;
      
      this.ws = new WebSocket(urlWithToken);
      
      this.ws.onopen = this.handleOpen.bind(this);
      this.ws.onmessage = this.handleMessage.bind(this);
      this.ws.onerror = this.handleError.bind(this);
      this.ws.onclose = this.handleClose.bind(this);
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      this.scheduleReconnect();
    }
  }

  /**
   * Close the current socket without treating the resulting onclose as
   * an unintentional disconnect (no reconnect should be scheduled for a
   * socket we are deliberately replacing/tearing down).
   */
  private closeCurrentSocket(): void {
    if (!this.ws) {
      return;
    }
    const staleSocket = this.ws;
    staleSocket.onclose = null;
    staleSocket.onerror = null;
    staleSocket.onmessage = null;
    staleSocket.onopen = null;
    if (staleSocket.readyState === WebSocket.OPEN || staleSocket.readyState === WebSocket.CONNECTING) {
      staleSocket.close();
    }
    this.ws = null;
  }

  /**
   * Disconnect from the WebSocket server
   */
  public disconnect(): void {
    this.isIntentionalClose = true;
    this.clearReconnectTimer();
    this.reconnectAttempts = 0;
    this.stopKeepalive();
    
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    // Reject all pending requests
    this.pendingRequests.forEach(({ reject, timeout }) => {
      clearTimeout(timeout);
      reject(new Error('WebSocket disconnected'));
    });
    this.pendingRequests.clear();
  }

  /**
   * Check if WebSocket is connected
   */
  public isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Send a message through the WebSocket
   */
  public send(message: WebSocketMessage): void {
    if (!this.ws) {
      console.error('WebSocket is not initialized');
      return;
    }

    if (this.ws.readyState !== WebSocket.OPEN) {
      console.error('WebSocket is not connected. State:', this.ws.readyState);
      return;
    }

    const messageWithTimestamp = {
      ...message,
      timestamp: Date.now()
    };

    this.ws.send(JSON.stringify(messageWithTimestamp));
    
    // Notify debug handlers
    if (this.debugMode) {
      this.debugHandlers.forEach(handler => handler('sent', messageWithTimestamp));
    }
  }

  /**
   * Send a function call response back to the server
   */
  public sendFunctionCallResponse(response: FunctionCallResponse): void {
    this.send({
      type: 'function_call_response',
      payload: response,
      requestId: response.requestId
    });
  }

  /**
   * Send a function call request and wait for response
   */
  public async callFunction(
    functionName: string,
    parameters: Record<string, any>,
    timeoutMs: number = 30000
  ): Promise<any> {
    const requestId = this.generateRequestId();
    
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.pendingRequests.delete(requestId);
        reject(new Error(`Function call timeout: ${functionName}`));
      }, timeoutMs);

      this.pendingRequests.set(requestId, { resolve, reject, timeout });

      this.send({
        type: 'function_call_request',
        payload: {
          functionName,
          parameters,
          requestId
        },
        requestId
      });
    });
  }

  /**
   * Register a message handler for a specific message type
   */
  public on(messageType: MessageType, handler: MessageHandler): () => void {
    if (!this.messageHandlers.has(messageType)) {
      this.messageHandlers.set(messageType, new Set());
    }
    
    this.messageHandlers.get(messageType)!.add(handler);

    // Return unsubscribe function
    return () => {
      const handlers = this.messageHandlers.get(messageType);
      if (handlers) {
        handlers.delete(handler);
      }
    };
  }

  /**
   * Register a connection handler
   */
  public onConnect(handler: ConnectionHandler): () => void {
    this.onConnectHandlers.add(handler);
    return () => this.onConnectHandlers.delete(handler);
  }

  /**
   * Register a disconnection handler
   */
  public onDisconnect(handler: ConnectionHandler): () => void {
    this.onDisconnectHandlers.add(handler);
    return () => this.onDisconnectHandlers.delete(handler);
  }

  /**
   * Register an error handler
   */
  public onError(handler: ErrorHandler): () => void {
    this.onErrorHandlers.add(handler);
    return () => this.onErrorHandlers.delete(handler);
  }

  /**
   * Register a debug handler for sent/received messages
   */
  public onDebug(handler: DebugHandler): () => void {
    this.debugHandlers.add(handler);
    return () => this.debugHandlers.delete(handler);
  }

  /**
   * Handle WebSocket open event
   */
  private handleOpen(): void {
    console.log('WebSocket connected');
    this.reconnectAttempts = 0;
    
    // Notify connection handlers
    this.onConnectHandlers.forEach(handler => handler());
    
    // Start keepalive ping/pong
    this.startKeepalive();
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleMessage(event: MessageEvent): void {
    try {
      const message: WebSocketMessage = JSON.parse(event.data);
      
      // Notify debug handlers
      if (this.debugMode) {
        this.debugHandlers.forEach(handler => handler('received', message));
      }
      
      // Handle function call responses for pending requests
      if (message.type === 'function_call_response' && message.requestId) {
        const pending = this.pendingRequests.get(message.requestId);
        if (pending) {
          clearTimeout(pending.timeout);
          this.pendingRequests.delete(message.requestId);
          
          if (message.payload.error) {
            pending.reject(new Error(message.payload.error));
          } else {
            pending.resolve(message.payload.result);
          }
          return;
        }
      }

      // Handle ping/pong for keepalive
      if (message.type === 'ping') {
        this.send({ type: 'pong', payload: {} });
        return;
      }

      // Notify registered handlers
      const handlers = this.messageHandlers.get(message.type);
      if (handlers) {
        handlers.forEach(handler => handler(message));
      }
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error);
    }
  }

  /**
   * Handle WebSocket error event
   */
  private handleError(event: Event): void {
    console.error('WebSocket error:', event);
    this.onErrorHandlers.forEach(handler => handler(event));
  }

  /**
   * Handle WebSocket close event
   */
  private handleClose(event: CloseEvent): void {
    console.log('WebSocket closed:', event.code, event.reason);
    
    // Notify disconnect handlers
    this.onDisconnectHandlers.forEach(handler => handler());

    // Attempt to reconnect if not intentional
    if (!this.isIntentionalClose) {
      this.scheduleReconnect();
    }
  }

  /**
   * Schedule a reconnection attempt. This is a persistent channel: we
   * never permanently give up. Backoff grows exponentially (capped at
   * maxReconnectDelay) with jitter so a token/apiHost that becomes valid
   * later (e.g. after a token refresh completes) is still picked up
   * without requiring a page reload. Retry count is reset on every
   * successful open and on every fresh (non-retry) connect() call.
   */
  private scheduleReconnect(): void {
    this.clearReconnectTimer();
    
    this.reconnectAttempts++;
    const exponentialDelay = Math.min(
      this.baseReconnectDelay * 2 ** (this.reconnectAttempts - 1),
      this.maxReconnectDelay
    );
    // Up to +20% jitter to avoid synchronized reconnect storms.
    const jitter = exponentialDelay * 0.2 * Math.random();
    const delay = Math.round(exponentialDelay + jitter);
    
    console.log(`Scheduling reconnect attempt ${this.reconnectAttempts} in ${delay}ms`);
    
    this.reconnectTimer = setTimeout(() => {
      console.log('Attempting to reconnect...');
      this.connect(this.apiHost, this.token, this.debugMode, /* isReconnectAttempt */ true);
    }, delay);
  }

  /**
   * Clear the reconnection timer
   */
  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * Start keepalive ping/pong
   */
  private startKeepalive(): void {
    this.stopKeepalive();
    
    this.keepaliveTimer = setInterval(() => {
      if (this.isConnected()) {
        this.send({
          type: 'ping',
          payload: { timestamp: Date.now() }
        });
      }
    }, this.keepaliveInterval);
  }

  /**
   * Stop keepalive ping/pong
   */
  private stopKeepalive(): void {
    if (this.keepaliveTimer) {
      clearInterval(this.keepaliveTimer);
      this.keepaliveTimer = null;
    }
  }

  /**
   * Generate a unique request ID
   */
  private generateRequestId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

// Export singleton instance
export const websocketService = new WebSocketService();
